import {
  AudioVideoComponent,
  FullwidthVideoComponent
} from '../../models/LessonComponent';
import React, { useEffect, useRef } from 'react';
import { cloneDeep, isEqual } from 'lodash';

import AssetService from '../../../shared/services/AssetService';
import { AssetType } from '../../../shared/models/Asset';
import { AudioVideoProgress } from '../../models/AudioVideoProgress';
import ChangeFile from '../../../shared/components/ChangeFile';
import LessonComponentService from '../../services/LessonComponentService';
import styles from '../../styles/AudioVideoPlayer.module.css';
import { useLessonContext } from '../../models/LessonContext';

interface AudioVideoPlayerProps {
  component: AudioVideoComponent | FullwidthVideoComponent;
  onSubmit: (value: string) => void; //* The 'value' is JSON of VideoStatus.
}

const initialStatus: AudioVideoProgress = {
  currentTime: 0,
  playedTime: 0,
  duration: 0
};

export function AudioVideoPlayer(props: AudioVideoPlayerProps) {
  const componentType = props.component.type;
  const assetkey = props.component.settings.assetkey;
  const blockForwardSeek = props.component.required;
  const audioVideoElement = useRef<HTMLMediaElement>(
    null
  ) as React.MutableRefObject<HTMLVideoElement>;
  const answer: AudioVideoProgress =
    (props.component.answer && JSON.parse(props.component.answer)) ||
    initialStatus;
  const { mode } = useLessonContext();
  const progressRef = useRef<AudioVideoProgress>(initialStatus);
  const currentTime = useRef(0);
  const playedTime = useRef(0);

  const handleUpdate = (value: number, key: keyof AudioVideoProgress) => {
    progressRef.current[key] = value;
  };

  const submitProgress = () => {
    if (
      LessonComponentService.canSubmit(mode) &&
      !isEqual(answer, progressRef.current)
    ) {
      props.onSubmit(JSON.stringify(progressRef.current));
    }
  };

  useEffect(() => {
    if (answer) {
      // Whenever the `progressRef` is changed the `answer` is also getting changed.
      // So do `cloneDeep` here.
      progressRef.current = cloneDeep(answer);
    }
  }, [answer]);

  useEffect(() => {
    audioVideoElement.current.currentTime = currentTime.current =
      progressRef.current.currentTime || 0;
    playedTime.current = progressRef.current.playedTime || 0;
  }, [progressRef.current, currentTime.current]);

  useEffect(() => {
    return function finalize() {
      if (
        LessonComponentService.canSubmit(mode) &&
        progressRef.current.playedTime
      ) {
        //* The Video component doesn't have the 'Save and Continue' button to submit an answer.
        //* It submits the progress JSON when it unmounts here in this function.
        submitProgress();
      }
    };
  }, [mode]);

  useEffect(() => {
    if (audioVideoElement.current) {
      const onDurationChange = (e: Event) =>
        handleUpdate((e.target as HTMLMediaElement).duration, 'duration');

      const onTimeUpdate = (e: Event) => {
        if (!audioVideoElement.current.seeking) {
          currentTime.current = audioVideoElement.current.currentTime;
          handleUpdate(currentTime.current, 'currentTime');
          if (playedTime.current < currentTime.current) {
            playedTime.current = currentTime.current;
            handleUpdate(playedTime.current, 'playedTime');
          }
        }
      };

      const onSeeking = (e: Event) => {
        const seekTime = (e.target as HTMLMediaElement)?.currentTime || 0;
        if (blockForwardSeek && seekTime > playedTime.current) {
          audioVideoElement.current.currentTime = currentTime.current;
        }
      };

      audioVideoElement.current.addEventListener(
        'durationchange',
        onDurationChange
      );
      audioVideoElement.current.addEventListener('timeupdate', onTimeUpdate);
      audioVideoElement.current.addEventListener('seeking', onSeeking);

      return () => {
        if (audioVideoElement.current) {
          audioVideoElement.current.removeEventListener(
            'durationchange',
            onDurationChange
          );
          audioVideoElement.current.removeEventListener(
            'timeupdate',
            onTimeUpdate
          );
          audioVideoElement.current.removeEventListener('seeking', onSeeking);
        }
      };
    }
  }, [audioVideoElement.current, currentTime.current, blockForwardSeek]);

  const handleEnded = () => {
    submitProgress();
  };

  const ChangeFileComponent = (assetType: AssetType) => {
    return (
      mode === 'edit' && (
        <ChangeFile
          component={props.component}
          assetkey={assetkey}
          assetTypes={[assetType]}
          style={{ bottom: assetType === 'audio' ? 0 : '' }}
        />
      )
    );
  };

  return (
    <>
      {(componentType === 'video' || componentType === 'full_width_video') && (
        <div className={styles.reactPlayerWrapper}>
          <video
            className={styles.reactPlayer}
            ref={audioVideoElement}
            controls
            controlsList="nodownload" //Hide 'Download' option in the video
            preload="auto"
            width="100%"
            height="100%"
            src={AssetService.getAssetUrl(assetkey) || ''}
            onEnded={handleEnded}
          />
          {ChangeFileComponent('video')}
        </div>
      )}
      {componentType === 'audio' && (
        <div className={styles.audioPlayerWrapper}>
          <audio
            ref={audioVideoElement}
            controls
            controlsList="nodownload"
            preload="auto"
            src={AssetService.getAssetUrl(assetkey) || ''}
            className={styles.audioPlayer}
            onEnded={handleEnded}
          />
          {ChangeFileComponent('audio')}
        </div>
      )}
    </>
  );
}
